import { useState } from "react";
import { Plus, Minus } from "lucide-react";

const FAQS = [
    {
        q: "What types of fire protection equipment do you offer?",
        a: "The Kitchensafe exhaust hood fire suppression system is all we offer — and it's all we focus on. We've engineered, refined, and certified one product for one job: protecting domestic-style hoods and ranges in commercial and institutional settings.",
    },
    {
        q: "How often should the Kitchensafe be inspected?",
        a: "Most states require a 6-month and a 1-year inspection. We provide free video training and certification so any licensed installer or in-house safety officer can perform compliant inspections.",
    },
    {
        q: "Do you offer installation services?",
        a: "Yes. We work with licensed, certified installers nationwide. Same-day install support is available, and every system ships ready for fire-marshal inspection — no extra modules, no scavenger-hunt parts list.",
    },
    {
        q: "What size cooking surface does Kitchensafe protect?",
        a: "Up to 48 inches wide. Single-nozzle systems cover a 30\" 4-burner range; dual-nozzle systems extend coverage to a full 48\" surface. Both are available in gas and electric configurations.",
    },
    {
        q: "How is Kitchensafe different from a standard wet-chem suppression system?",
        a: "Three things: (1) a two-stage heat sensor that warns staff before activation, helping avoid unnecessary shutdowns; (2) fixed nozzles that can't drift during cleaning; and (3) a self-contained control panel that doubles as a fire alarm — no external modules required.",
    },
    {
        q: "Can the control panel be mounted away from the hood?",
        a: "Yes. The electronic control panel can be mounted up to 15 feet from the hood, with all terminals self-contained. This makes Kitchensafe easy to spec into existing layouts.",
    },
    {
        q: "What happens when the system activates?",
        a: "On activation, Kitchensafe automatically disconnects the gas (via solenoid) or electric (via relay) source feeding the appliance, fires wet-chemical suppressant through the manifold, and signals the central alarm terminal — all in seconds.",
    },
];

function FAQHeader() {
    return (
        <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-6">
                <span className="font-mono-tech text-[11px] uppercase tracking-[0.22em] text-[#E53935]">
                    / 05 — Questions
                </span>
            </div>
            <h2 className="font-display font-extrabold text-4xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
                Asked &<br />
                <span className="text-[#E53935] italic font-medium">answered</span>.
            </h2>
            <p className="mt-8 text-base text-[#a1a1aa] leading-relaxed">
                Don't see your question? Reach Tom directly — he answers every call and text
                personally.
            </p>
            <a
                href="tel:4806945234"
                data-testid="faq-call-cta"
                className="mt-6 inline-flex items-center gap-2 font-mono-tech text-sm text-white border-b border-[#E53935] pb-1 hover:text-[#E53935] transition-colors"
            >
                480-694-5234
            </a>
        </div>
    );
}

function FAQItem({ item, index, isOpen, onToggle }) {
    return (
        <div className="border-b border-white/10" data-testid={`faq-item-${index}`}>
            <button
                onClick={onToggle}
                className="w-full py-6 flex items-start justify-between gap-6 text-left group"
                data-testid={`faq-toggle-${index}`}
            >
                <span className="flex items-start gap-4 flex-1">
                    <span className="font-mono-tech text-[11px] text-[#71717a] mt-1.5 tabular-nums">
                        {String(index + 1).padStart(2, "0")}
                    </span>
                    <span className="font-display font-bold text-base md:text-lg text-white group-hover:text-[#E53935] transition-colors">
                        {item.q}
                    </span>
                </span>
                <span className="w-8 h-8 flex items-center justify-center border border-white/10 group-hover:border-[#E53935] transition-colors flex-shrink-0">
                    {isOpen ? (
                        <Minus className="w-3.5 h-3.5 text-[#E53935]" />
                    ) : (
                        <Plus className="w-3.5 h-3.5 text-white" />
                    )}
                </span>
            </button>
            <div
                className={`grid transition-all duration-300 ease-out ${
                    isOpen ? "grid-rows-[1fr] opacity-100 pb-6" : "grid-rows-[0fr] opacity-0"
                }`}
            >
                <div className="overflow-hidden">
                    <p className="pl-9 pr-12 text-sm md:text-base text-[#a1a1aa] leading-relaxed">
                        {item.a}
                    </p>
                </div>
            </div>
        </div>
    );
}

export default function FAQ() {
    const [openIndex, setOpenIndex] = useState(0);

    return (
        <section
            id="faq"
            data-testid="faq-section"
            className="relative py-20 md:py-24 border-t border-white/10 bg-[#0a0a0b]"
        >
            <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
                <div className="grid lg:grid-cols-12 gap-12">
                    <FAQHeader />
                    <div className="lg:col-span-7">
                        <div className="border-t border-white/10">
                            {FAQS.map((item, i) => (
                                <FAQItem
                                    key={item.q}
                                    item={item}
                                    index={i}
                                    isOpen={openIndex === i}
                                    onToggle={() => setOpenIndex(openIndex === i ? -1 : i)}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
